import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../../utils/constants";
import { Form } from "react-bootstrap";
import * as XLSX from "xlsx";
import "../ReferenceDraftReports.css";

const PROCESS_MAP = {
  "Appointment Letter":     1,
  "Acceptance Letter":      2,
  "SOC":                    3,
  "Section 17 Application": 4,
  "Section 17 Order":       5,
  "Second Hearing":         6,
  "Third Hearing":          7,
  "Award Pass":             8,
};

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
};

const DeliveryCell = ({ status, sentDate }) => {
  const sent = status !== null && status !== undefined;
  return (
    <>
      <td className={sent ? "statusSend border" : "statusPending border"}>
        {sent ? "Sent" : "Pending"}
      </td>
      <td className="border" style={{ fontSize: "12px", color: "#495057" }}>
        {fmtDate(sentDate)}
      </td>
    </>
  );
};

const ServiceReports = () => {
  const [data, setData]                       = useState([]);
  const [clients, setClients]                 = useState([]);
  const [products, setProducts]               = useState([]);
  const [selectedClientID, setSelectedClientID] = useState("");
  const [selectedProductID, setSelectedProductID] = useState("");
  const [selectedLotNo, setSelectedLotNo]     = useState("");
  const [processType, setProcessType]         = useState("");
  const [showTable, setShowTable]             = useState(false);
  const [loading, setLoading]                 = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/Client`)
      .then((r) => r.json())
      .then((result) => setClients(Array.isArray(result) ? result : []))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedClientID) { setProducts([]); setSelectedProductID(""); return; }
    fetch(`${API_BASE_URL}/api/products?client_id=${selectedClientID}`)
      .then((r) => r.json())
      .then((result) => setProducts(Array.isArray(result) ? result : []))
      .catch(console.error);
  }, [selectedClientID]);

  const processId = PROCESS_MAP[processType] ?? null;

  const handleData = async () => {
    if (!selectedClientID || !selectedProductID || !selectedLotNo || !processId) {
      alert("Please select all fields");
      return;
    }
    setLoading(true);
    setShowTable(false);
    setData([]);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/ServiceData?Lot_no=${selectedLotNo}&Client_id=${selectedClientID}&Product_id=${selectedProductID}&Process_id=${processId}`
      );
      const result = await res.json();
      setData(Array.isArray(result) ? result : []);
      setShowTable(true);
    } catch (err) {
      console.error(err);
      alert("Failed to load service report data");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedClientID("");
    setSelectedProductID("");
    setSelectedLotNo("");
    setProcessType("");
    setData([]);
    setShowTable(false);
  };

  const exportToExcel = () => {
    if (!data.length) return;
    const rows = data.map((r, i) => ({
      "Sr No":         i + 1,
      "Name":          r.Cust_name,
      "Reference No":  r.Reference_no,
      "Mobile No":     r.Mobile_no,
      "Email":         r.Email_id,
      "WA Status":     r.wa_status   !== null ? "Sent" : "Pending",
      "WA Date":       fmtDate(r.wa_send_date),
      "Mail Status":   r.mail_status !== null ? "Sent" : "Pending",
      "Mail Date":     fmtDate(r.mail_send_date),
      "SMS Status":    r.sms_status  !== null ? "Sent" : "Pending",
      "SMS Date":      fmtDate(r.sms_send_date),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Service_Report");
    XLSX.writeFile(wb, `${processType} Service Report.xlsx`);
  };

  return (
    <div className="container">
      <h3 className="ps-3 pt-3">Service Reports</h3>

      {/* Filters */}
      <div className="row align-items-center justify-content-evenly my-3 g-2">
        <div className="col-md-2">
          <Form.Select
            value={selectedClientID}
            onChange={(e) => setSelectedClientID(e.target.value)}
          >
            <option value="">Choose Bank</option>
            {clients.map((item) => (
              <option key={item.Client_id} value={item.Client_id}>
                {item.client_name}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="col-md-2">
          <Form.Select
            value={selectedProductID}
            onChange={(e) => setSelectedProductID(e.target.value)}
            disabled={!selectedClientID}
          >
            <option value="">Choose Product</option>
            {products.map((item) => (
              <option key={item.Product_id} value={item.Product_id}>
                {item.Product_name}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="col-md-2">
          <Form.Control
            type="number"
            placeholder="Enter Lot No"
            value={selectedLotNo}
            onChange={(e) => setSelectedLotNo(e.target.value)}
          />
        </div>

        <div className="col-md-2">
          <Form.Select
            value={processType}
            onChange={(e) => setProcessType(e.target.value)}
          >
            <option value="">Select Process</option>
            {Object.keys(PROCESS_MAP).map((key) => (
              <option key={key} value={key}>{key}</option>
            ))}
          </Form.Select>
        </div>

        <div className="col-md-1">
          <button
            className="custBtn w-100"
            onClick={handleData}
            disabled={loading || !selectedClientID || !selectedProductID || !selectedLotNo || !processType}
          >
            {loading ? "..." : "Show"}
          </button>
        </div>

        <div className="col-md-1">
          <button className="btn btn-outline-secondary w-100" onClick={handleClear}>
            Clear
          </button>
        </div>
      </div>

      {/* Export */}
      {showTable && data.length > 0 && (
        <div className="text-end mb-2">
          <button className="custBtn" onClick={exportToExcel}>
            Export Excel
          </button>
        </div>
      )}

      {/* Empty state */}
      {showTable && !loading && data.length === 0 && (
        <div className="alert alert-light border text-center mt-3">
          No service records found for the selected filters.
        </div>
      )}

      {/* Table */}
      {showTable && data.length > 0 && (
        <div className="table-container mt-3">
          <div className="table-wrapper">
            <table className="responsive-table">
              <thead>
                <tr className="text-center border">
                  <th rowSpan={2} className="border">Sr No</th>
                  <th rowSpan={2} className="border">Name</th>
                  <th colSpan={2} className="text-center border">WhatsApp</th>
                  <th colSpan={2} className="text-center border">Email</th>
                  <th colSpan={2} className="text-center border">SMS</th>
                  <th rowSpan={2} className="border">Mobile No</th>
                  <th rowSpan={2} className="border">Email ID</th>
                  <th rowSpan={2} className="border">Reference No</th>
                </tr>
                <tr className="text-center">
                  <th className="border">Status</th>
                  <th className="border">Date</th>
                  <th className="border">Status</th>
                  <th className="border">Date</th>
                  <th className="border">Status</th>
                  <th className="border">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={index} className="text-center">
                    <td className="border">{index + 1}</td>
                    <td className="border">{row.Cust_name}</td>

                    <DeliveryCell status={row.wa_status}   sentDate={row.wa_send_date}   />
                    <DeliveryCell status={row.mail_status} sentDate={row.mail_send_date} />
                    <DeliveryCell status={row.sms_status}  sentDate={row.sms_send_date}  />

                    <td className="border">{row.Mobile_no}</td>
                    <td className="border">{row.Email_id}</td>
                    <td className="border">{row.Reference_no}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceReports;
